import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Select from "react-select";
import { Plus, X, ArrowUpDown, Search, Pencil, Trash2, Mail, Paperclip, Send, FileSpreadsheet, Users, Eye } from 'lucide-react';
import { IoLogoWhatsapp } from 'react-icons/io5';
import { clientsAPI, employeesAPI } from "../../../services/api";
import { bulkEmailAPI, templatesAPI } from "../../../services/api";
import { getWhatsAppNumber, getWhatsAppNumberSource } from '../../Templates/templateUtils';
import EmailTemplatePanel from '../../Templates/EmailTemplatePanel';
import WhatsAppTemplatePanel from '../../Templates/WhatsAppTemplatePanel';
import { exportClientsToExcel } from "./ExportClient";
import ClientPreview from "./ClientPreview";
import CreateClientModal from "./CreateClientModal";
import ImportClientsModal from "./ImportClientsModal";
import DeleteClientModal from "./DeleteClientModal";
import AssignClientModal from "./AssignClientModal";
import EditClientModal from "./EditClientModal";
import ClientEditDrawer from "./ClientEditDrawer";
import PageNavigator from "../../common/PageNavigator";
import { toast } from "react-toastify";

const BRAND = "#1C4E6D";
const BRAND_DARK = "#153a4f";
const PAGE_SIZE = 20;

// ── BulkEmailModal ───────────────────────────────────────────────────────────
const BulkEmailModal = ({ selectedCount, selectedIds, entityType, onClose }) => {
  const [subject, setSubject]         = useState('');
  const [body, setBody]               = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending]         = useState(false);
  const [templates, setTemplates]     = useState([]);
  const [showPicker, setShowPicker]   = useState(false);
  const [loadingTpl, setLoadingTpl]   = useState(false);
  const fileRef = useRef();

  const openTemplatePicker = async () => {
    if (showPicker) { setShowPicker(false); return; }
    if (templates.length === 0) {
      setLoadingTpl(true);
      try {
        const res = await templatesAPI.getTemplates(null, 'EMAIL', 'CRM');
        setTemplates(res.data || []);
      } catch { toast.error('Failed to load templates'); }
      finally { setLoadingTpl(false); }
    }
    setShowPicker(true);
  };

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject || '');
    setBody(tpl.body || '');
    setShowPicker(false);
  };

  const handleSend = async () => {
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    if (!body.trim())    { toast.error('Email body is required'); return; }
    setSending(true);
    try {
      const res = await bulkEmailAPI.sendByIds(entityType, selectedIds, subject, body, attachments);
      const d = res.data;
      toast.success(`Sent to ${d.successCount}/${d.totalRecipients} recipients`);
      if (d.failedCount > 0) toast.warning(`${d.failedCount} failed`);
      if (d.skippedCount > 0) toast.info(`${d.skippedCount} recipient(s) skipped (do-not-email, invalid email, or limit)`);
      onClose();
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.message || err.message));
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col" style={{ maxHeight: '90vh' }}>
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-0.5">Bulk Email</p>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Users size={16} className="text-slate-400" />
              Send to {selectedCount} selected {selectedCount === 1 ? 'client' : 'clients'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <button onClick={openTemplatePicker} disabled={loadingTpl}
              className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 disabled:opacity-50">
              {loadingTpl ? 'Loading…' : showPicker ? '▲ Hide templates' : '▼ Use a template'}
            </button>
            {showPicker && (
              <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden">
                {templates.length === 0 ? (
                  <p className="px-3 py-2.5 text-xs text-slate-400">No email templates found. Create one in My Services → Templates.</p>
                ) : (
                  templates.map(t => (
                    <button key={t.id} onClick={() => applyTemplate(t)}
                      className="w-full text-left px-3 py-2.5 text-xs hover:bg-blue-50 border-b border-slate-100 last:border-0 transition-colors">
                      <div className="font-semibold text-slate-800">{t.name}</div>
                      {t.subject && <div className="text-slate-400 truncate mt-0.5">{t.subject}</div>}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Subject <span className="text-red-500">*</span></label>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Enter email subject..."
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Message <span className="text-red-500">*</span></label>
            <textarea rows={7} value={body} onChange={e => setBody(e.target.value)} placeholder="Write your email message here... (HTML supported)"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Attachments <span className="text-slate-400 font-normal normal-case">(optional)</span></label>
            <div onClick={() => fileRef.current.click()} className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
              <Paperclip size={15} className="text-slate-400" /><span className="text-sm text-slate-400">Click to attach files</span>
            </div>
            <input ref={fileRef} type="file" multiple className="hidden" onChange={e => setAttachments(prev => [...prev, ...Array.from(e.target.files)])} />
            {attachments.length > 0 && (
              <div className="mt-2 space-y-1">
                {attachments.map((f, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5 bg-slate-50 rounded-lg border text-xs">
                    <span className="text-slate-600 truncate">{f.name}</span>
                    <button onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600 ml-2"><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex gap-3 flex-shrink-0">
          <button onClick={onClose} disabled={sending} className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          <button onClick={handleSend} disabled={sending} className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
            {sending ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sending…</> : <><Send size={14} />Send Email</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── ExcelEmailModal ──────────────────────────────────────────────────────────
const ExcelEmailModal = ({ entityType, onClose }) => {
  const [subject, setSubject]         = useState('');
  const [body, setBody]               = useState('');
  const [excelFile, setExcelFile]     = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending]         = useState(false);
  const [templates, setTemplates]     = useState([]);
  const [showPicker, setShowPicker]   = useState(false);
  const [loadingTpl, setLoadingTpl]   = useState(false);
  const excelRef  = useRef();
  const attachRef = useRef();

  const openTemplatePicker = async () => {
    if (showPicker) { setShowPicker(false); return; }
    if (templates.length === 0) {
      setLoadingTpl(true);
      try {
        const res = await templatesAPI.getTemplates(null, 'EMAIL', 'CRM');
        setTemplates(res.data || []);
      } catch { toast.error('Failed to load templates'); }
      finally { setLoadingTpl(false); }
    }
    setShowPicker(true);
  };

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject || '');
    setBody(tpl.body || '');
    setShowPicker(false);
  };

  const handleSend = async () => {
    if (!excelFile)      { toast.error('Please upload an Excel file'); return; }
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    if (!body.trim())    { toast.error('Email body is required'); return; }
    setSending(true);
    try {
      const res = await bulkEmailAPI.sendByExcel(entityType, excelFile, subject, body, attachments);
      const d = res.data;
      toast.success(`Sent to ${d.successCount}/${d.totalRecipients} recipients`);
      if (d.failedCount > 0) toast.warning(`${d.failedCount} failed`);
      if (d.skippedCount > 0) toast.info(`${d.skippedCount} recipient(s) skipped (do-not-email, invalid email, or limit)`);
      if (d.excelRowErrors?.length > 0) toast.info(`${d.excelRowErrors.length} row(s) skipped from Excel (missing/invalid email)`);
      onClose();
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.message || err.message));
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col" style={{ maxHeight: '90vh' }}>
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-0.5">Excel Email</p>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2"><FileSpreadsheet size={16} className="text-slate-400" />Send Email via Excel Upload</h2>
            <p className="text-xs text-slate-400 mt-0.5">Excel must have an <strong>email</strong> column</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Excel File (.xlsx / .xls) <span className="text-red-500">*</span></label>
            {excelFile ? (
              <div className="flex items-center justify-between px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center gap-2"><FileSpreadsheet size={15} className="text-emerald-600" /><span className="text-sm font-medium text-emerald-800 truncate">{excelFile.name}</span></div>
                <button onClick={() => setExcelFile(null)} className="text-red-400 hover:text-red-600 ml-2"><X size={14} /></button>
              </div>
            ) : (
              <div onClick={() => excelRef.current.click()} className="flex flex-col items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-emerald-400 hover:bg-emerald-50 transition-colors">
                <FileSpreadsheet size={24} className="text-slate-300" />
                <span className="text-sm text-slate-400">Click to upload Excel file</span>
                <span className="text-xs text-slate-300">.xlsx or .xls only</span>
              </div>
            )}
            <input ref={excelRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={e => e.target.files[0] && setExcelFile(e.target.files[0])} />
          </div>
          <div>
            <button onClick={openTemplatePicker} disabled={loadingTpl}
              className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 disabled:opacity-50">
              {loadingTpl ? 'Loading…' : showPicker ? '▲ Hide templates' : '▼ Use a template'}
            </button>
            {showPicker && (
              <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden">
                {templates.length === 0 ? (
                  <p className="px-3 py-2.5 text-xs text-slate-400">No email templates found. Create one in My Services → Templates.</p>
                ) : (
                  templates.map(t => (
                    <button key={t.id} onClick={() => applyTemplate(t)}
                      className="w-full text-left px-3 py-2.5 text-xs hover:bg-blue-50 border-b border-slate-100 last:border-0 transition-colors">
                      <div className="font-semibold text-slate-800">{t.name}</div>
                      {t.subject && <div className="text-slate-400 truncate mt-0.5">{t.subject}</div>}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Subject <span className="text-red-500">*</span></label>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Enter email subject..."
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Message <span className="text-red-500">*</span></label>
            <textarea rows={7} value={body} onChange={e => setBody(e.target.value)} placeholder="Write your email message here... (HTML supported)"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Attachments <span className="text-slate-400 font-normal normal-case">(optional)</span></label>
            <div onClick={() => attachRef.current.click()} className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-emerald-400 hover:bg-emerald-50 transition-colors">
              <Paperclip size={15} className="text-slate-400" /><span className="text-sm text-slate-400">Click to attach files</span>
            </div>
            <input ref={attachRef} type="file" multiple className="hidden" onChange={e => setAttachments(prev => [...prev, ...Array.from(e.target.files)])} />
            {attachments.length > 0 && (
              <div className="mt-2 space-y-1">
                {attachments.map((f, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5 bg-slate-50 rounded-lg border text-xs">
                    <span className="text-slate-600 truncate">{f.name}</span>
                    <button onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600 ml-2"><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex gap-3 flex-shrink-0">
          <button onClick={onClose} disabled={sending} className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          <button onClick={handleSend} disabled={sending} className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
            {sending ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sending…</> : <><Send size={14} />Send Email</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Client Component ────────────────────────────────────────────────────
const Client = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('All clients');
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [showExcelEmailModal, setShowExcelEmailModal] = useState(false);
  const [selectedClientsForEdit, setSelectedClientsForEdit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedClientForPreview, setSelectedClientForPreview] = useState(null);
  const [employeeNameCache, setEmployeeNameCache] = useState({});
  const [editDrawerClient, setEditDrawerClient] = useState(null);
  const [singleDeleteClient, setSingleDeleteClient] = useState(null);
  const [showSingleDeleteModal, setShowSingleDeleteModal] = useState(false);
  const [emailPanelClient, setEmailPanelClient] = useState(null);
  const [whatsappPanelClient, setWhatsappPanelClient] = useState(null);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('clientViewColumns');
    return saved ? JSON.parse(saved) : [
      'companyName', 'websiteUrl', 'industry', 'relationshipType',
      'leadStatus', 'leadSource', 'primaryContact', 'email',
      'phone', 'contactOwner', 'createdAt'
    ];
  });

  const allColumns = [
    { key: 'companyName',      label: 'Company Name',      required: true },
    { key: 'websiteUrl',       label: 'Website' },
    { key: 'industry',         label: 'Industry' },
    { key: 'relationshipType', label: 'Relationship Type' },
    { key: 'leadStatus',       label: 'Lead Status' },
    { key: 'leadSource',       label: 'Lead Source' },
    { key: 'linkedInUrl',      label: 'LinkedIn' },
    { key: 'companyStage',     label: 'Company Stage' },
    { key: 'legalType',        label: 'Legal Type' },
    { key: 'employeeCount',    label: 'Employee Count' },
    { key: 'annualRevenue',    label: 'Annual Revenue' },
    { key: 'primaryContact',   label: 'Primary Contact' },
    { key: 'jobTitle',         label: 'Job Title' },
    { key: 'email',            label: 'Email' },
    { key: 'phone',            label: 'Phone' },
    { key: 'contactOwner',     label: 'Assigned To' },
    { key: 'createdAt',        label: 'Create Date' },
    { key: 'updatedAt',        label: 'Last Modified' },
  ];

  const [filters, setFilters] = useState({
    industry: null, leadStatus: null, leadSource: null,
    relationshipType: null, companyStage: null, legalType: null,
    employeeCount: null, annualRevenue: null, paymentTerms: null,
    companyName: '', websiteUrl: '', email: '', phone: '',
    primaryContact: '', jobTitle: '', contactOwner: null
  });

  const tabs = ['All clients', 'My clients'];

  const industryOptions = [
    { value: 'IT', label: 'IT' }, { value: 'FINANCE', label: 'Finance' },
    { value: 'HEALTHCARE', label: 'Healthcare' }, { value: 'EDUCATION', label: 'Education' },
    { value: 'MANUFACTURING', label: 'Manufacturing' }, { value: 'RETAIL', label: 'Retail' },
    { value: 'OTHER', label: 'Other' },
  ];
  const leadStatusOptions = [
    { value: 'NEW', label: 'New' }, { value: 'CONTACTED', label: 'Contacted' },
    { value: 'QUALIFIED', label: 'Qualified' }, { value: 'LOST', label: 'Lost' },
    { value: 'WON', label: 'Won' }, { value: 'OTHER', label: 'Other' },
  ];
  const leadSourceOptions = [
    { value: 'WEBSITE', label: 'Website' }, { value: 'REFERRAL', label: 'Referral' },
    { value: 'PARTNER', label: 'Partner' }, { value: 'SOCIAL_MEDIA', label: 'Social Media' },
    { value: 'EMAIL', label: 'Email' }, { value: 'CALL', label: 'Call' }, { value: 'OTHER', label: 'Other' },
  ];
  const relationshipTypeOptions = [
    { value: 'CLIENT', label: 'Client' }, { value: 'PARTNER', label: 'Partner' },
    { value: 'SUPPLIER', label: 'Supplier' }, { value: 'VENDOR', label: 'Vendor' },
    { value: 'DISTRIBUTOR', label: 'Distributor' }, { value: 'RESELLER', label: 'Reseller' },
    { value: 'CONTRACTOR', label: 'Contractor' }, { value: 'CONSULTANT', label: 'Consultant' },
    { value: 'SERVICE_PROVIDER', label: 'Service Provider' }, { value: 'INVESTOR', label: 'Investor' },
    { value: 'MANUFACTURER', label: 'Manufacturer' }, { value: 'OTHER', label: 'Other' },
  ];
  const companyStageOptions = [
    { value: 'STARTUP', label: 'Startup' }, { value: 'SMALL', label: 'Small' },
    { value: 'MEDIUM', label: 'Medium' }, { value: 'LARGE', label: 'Large' },
    { value: 'ENTERPRISE', label: 'Enterprise' }, { value: 'OTHER', label: 'Other' },
  ];
  const legalTypeOptions = [
    { value: 'SOLE_PROPRIETORSHIP', label: 'Sole Proprietorship' },
    { value: 'PARTNERSHIP_FIRM', label: 'Partnership Firm' }, { value: 'LLP', label: 'LLP' },
    { value: 'PRIVATE_LIMITED', label: 'Private Limited' }, { value: 'PUBLIC_LIMITED', label: 'Public Limited' },
    { value: 'OPC', label: 'OPC' }, { value: 'JOINT_VENTURE', label: 'Joint Venture' },
    { value: 'NON_PROFIT', label: 'Non-Profit' }, { value: 'GOVERNMENT_ENTITY', label: 'Government Entity' },
    { value: 'OTHER', label: 'Other' },
  ];
  const employeeCountOptions = [
    { value: 'RANGE_1_10', label: '1–10' }, { value: 'RANGE_11_50', label: '11–50' },
    { value: 'RANGE_51_100', label: '51–100' }, { value: 'RANGE_101_250', label: '101–250' },
    { value: 'RANGE_251_500', label: '251–500' }, { value: 'RANGE_501_1000', label: '501–1,000' },
    { value: 'RANGE_1001_5000', label: '1,001–5,000' }, { value: 'RANGE_5001_10000', label: '5,001–10,000' },
    { value: 'RANGE_10000_PLUS', label: '10,000+' },
  ];
  const annualRevenueOptions = [
    { value: 'BELOW_1M', label: 'Below $1M' }, { value: 'RANGE_1M_5M', label: '$1M – $5M' },
    { value: 'RANGE_5M_10M', label: '$5M – $10M' }, { value: 'RANGE_10M_50M', label: '$10M – $50M' },
    { value: 'RANGE_50M_100M', label: '$50M – $100M' }, { value: 'RANGE_100M_500M', label: '$100M – $500M' },
    { value: 'RANGE_500M_1B', label: '$500M – $1B' }, { value: 'RANGE_1B_5B', label: '$1B – $5B' },
    { value: 'RANGE_5B_10B', label: '$5B – $10B' }, { value: 'ABOVE_10B', label: 'Above $10B' },
  ];
  const paymentTermsOptions = [
    { value: 'NET15', label: 'Net 15' }, { value: 'NET30', label: 'Net 30' },
    { value: 'NET45', label: 'Net 45' }, { value: 'NET60', label: 'Net 60' },
    { value: 'NET90', label: 'Net 90' }, { value: 'COD', label: 'Cash on Delivery' },
    { value: 'PREPAID', label: 'Prepaid' }, { value: 'LC', label: 'Letter of Credit' },
    { value: 'PAYMENT_ON_RECEIPT', label: 'Payment on Receipt' }, { value: 'MONTHLY', label: 'Monthly' },
    { value: 'QUARTERLY', label: 'Quarterly' }, { value: 'ANNUAL', label: 'Annual' },
    { value: 'OTHER', label: 'Other' },
  ];

  useEffect(() => {
    const ids = [...new Set(clients.map(c => c.contactOwner).filter(Boolean))];
    const uncachedIds = ids.filter(id => !(id in employeeNameCache));
    if (uncachedIds.length === 0) return;
    uncachedIds.forEach(async (id) => {
      try {
        const response = await employeesAPI.getEmployeeById(id);
        const emp = response.data;
        const name = emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.email || id;
        setEmployeeNameCache(prev => ({ ...prev, [id]: name }));
      } catch { setEmployeeNameCache(prev => ({ ...prev, [id]: id })); }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients]);

  const fetchEmployeeOptions = async () => {
    try {
      const response = await employeesAPI.getAllEmployees();
      const options = (response.data || []).map(emp => ({
        value: emp.id,
        label: emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.email || emp.id
      }));
      setEmployeeOptions(options);
    } catch (error) { console.error('Failed to fetch employees:', error); }
  };

  const fetchClientsPaged = async (page) => {
    setLoading(true);
    try {
      const params = {
        page, size: PAGE_SIZE,
        search: searchQuery || undefined,
        industry: filters.industry?.value,
        leadStatus: filters.leadStatus?.value,
        leadSource: filters.leadSource?.value,
        relationshipType: filters.relationshipType?.value,
        companyStage: filters.companyStage?.value,
        legalType: filters.legalType?.value,
        employeeCount: filters.employeeCount?.value,
        annualRevenue: filters.annualRevenue?.value,
        paymentTerms: filters.paymentTerms?.value,
        companyName: filters.companyName || undefined,
        websiteUrl: filters.websiteUrl || undefined,
        email: filters.email || undefined,
        phone: filters.phone || undefined,
        primaryContact: filters.primaryContact || undefined,
        jobTitle: filters.jobTitle || undefined,
        contactOwner: filters.contactOwner?.value,
        ...(activeTab === 'My clients' ? { scope: 'mine' } : {})
      };
      const response = await clientsAPI.getClientsPaged(params);
      setClients(response.data.content || []);
      setCurrentPage(response.data.currentPage);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements);
    } catch (error) { toast.error('Failed to fetch clients'); }
    finally { setLoading(false); }
  };

  const refreshClients = () => fetchClientsPaged(currentPage);

  useEffect(() => { fetchEmployeeOptions(); }, []);

  useEffect(() => {
    fetchClientsPaged(currentPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeTab, searchQuery, currentPage]);

  useEffect(() => {
    if (location.state?.openCreate) {
      setShowCreateModal(true);
    }
  }, [location.state]);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(0);
  };
  const clearAllFilters = () => {
    setFilters({ industry: null, leadStatus: null, leadSource: null, relationshipType: null, companyStage: null, legalType: null, employeeCount: null, annualRevenue: null, paymentTerms: null, companyName: '', websiteUrl: '', email: '', phone: '', primaryContact: '', jobTitle: '', contactOwner: null });
    setCurrentPage(0);
  };
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(0);
  };

  const handleSelectAll = () => setSelectedRows(selectedRows.length === clients.length ? [] : clients.map(c => c.id));
  const handleRowSelect = (id) => setSelectedRows(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);

  const handleBulkDelete = async () => {
    try {
      if (selectedRows.length === 1) {
        await clientsAPI.deleteClient(selectedRows[0]);
        toast.success('Client deleted successfully');
      } else {
        const res = await clientsAPI.bulkDeleteClients(selectedRows);
        const r = res.data || {};
        if (r.failedCount > 0) toast.warning(`${r.successCount} deleted, ${r.failedCount} could not be deleted (still in use)`);
        else toast.success(`${r.successCount ?? selectedRows.length} client(s) deleted successfully`);
      }
      setSelectedRows([]); setShowDeleteModal(false); refreshClients();
    } catch (error) { toast.error(error.response?.data?.message || 'Failed to delete client(s)'); }
  };

  const handleSingleDelete = async () => {
    if (!singleDeleteClient) return;
    try {
      await clientsAPI.deleteClient(singleDeleteClient.id);
      toast.success('Client deleted successfully');
      setShowSingleDeleteModal(false); setSingleDeleteClient(null); refreshClients();
    } catch (error) { toast.error(error.response?.data?.message || 'Failed to delete client'); }
  };

  const handleAssign = async (payload) => {
    try {
      await clientsAPI.bulkAssignClients(payload);
      toast.success('Client(s) assigned successfully');
      setSelectedRows([]); setShowAssignModal(false); refreshClients();
    } catch { toast.error('Failed to assign client(s)'); }
  };

  const handleBulkEdit = async (payload) => {
    try {
      const res = await clientsAPI.bulkEditClients(payload);
      const r = res.data || {};
      if (r.failedCount > 0) toast.warning(`${r.successCount} updated, ${r.failedCount} failed`);
      else toast.success(`${r.successCount ?? ''} Client(s) updated successfully`.trim());
      setSelectedRows([]); setShowEditModal(false); setSelectedClientsForEdit([]); refreshClients();
    } catch (error) { toast.error(error.response?.data?.message || 'Failed to update client(s)'); }
  };

  const handleExport = () => {
    const dataToExport = selectedRows.length > 0 ? clients.filter(c => selectedRows.includes(c.id)) : clients;
    exportClientsToExcel(dataToExport);
  };

  const isColumnVisible = (key) => visibleColumns.includes(key);

  const handleColumnToggle = (columnKey) => {
    const column = allColumns.find(col => col.key === columnKey);
    if (column?.required) return;
    setVisibleColumns(prev => { const next = prev.includes(columnKey) ? prev.filter(k => k !== columnKey) : [...prev, columnKey]; localStorage.setItem('clientViewColumns', JSON.stringify(next)); return next; });
  };

  const handleSelectAllColumns = () => { const allKeys = allColumns.map(c => c.key); setVisibleColumns(allKeys); localStorage.setItem('clientViewColumns', JSON.stringify(allKeys)); };
  const handleDeselectAllColumns = () => { const required = allColumns.filter(c => c.required).map(c => c.key); setVisibleColumns(required); localStorage.setItem('clientViewColumns', JSON.stringify(required)); };

  const renderCellContent = (column, client) => {
    switch (column.key) {
      case 'companyName': {
        const cName = client.companyName || '';
        return (
          <div className="flex items-center gap-2">
            <div
              className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
              style={{ backgroundColor: BRAND }}
            >
              {cName ? cName.charAt(0).toUpperCase() : '?'}
            </div>
            <button
              onClick={e => { e.stopPropagation(); navigate(`/clients/${client.id}`); }}
              className="text-sm font-medium hover:underline cursor-pointer"
              style={{ color: BRAND }}
              onMouseEnter={e => e.currentTarget.style.color = BRAND_DARK}
              onMouseLeave={e => e.currentTarget.style.color = BRAND}
            >
              {cName || <span className="text-gray-400 italic">Unnamed</span>}
            </button>
          </div>
        );
      }
      case 'contactOwner': {
        const id = client.contactOwner;
        if (!id) return <span className="text-gray-400">—</span>;
        const name = employeeNameCache[id];
        if (!name) return <span className="flex items-center gap-1 text-gray-400 text-xs"><div className="w-3 h-3 border border-gray-300 border-t-transparent rounded-full animate-spin" />Loading...</span>;
        return <div className="flex items-center gap-2"><div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-indigo-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{name.charAt(0).toUpperCase()}</div><span className="text-sm text-gray-800">{name}</span></div>;
      }
      case 'websiteUrl': return client.websiteUrl ? <a href={`https://${client.websiteUrl}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline" onClick={e => e.stopPropagation()}>{client.websiteUrl}</a> : null;
      case 'linkedInUrl': return client.linkedInUrl ? <a href={client.linkedInUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline" onClick={e => e.stopPropagation()}>View Profile</a> : null;
      case 'email':
        return client.email ? (
          <div className="flex items-center gap-1.5">
            <a href={`mailto:${client.email}`} className="text-blue-600 hover:underline" onClick={e => e.stopPropagation()}>{client.email}</a>
            <button onClick={e => { e.stopPropagation(); setEmailPanelClient(client); }} title="Send email from template"
              className="opacity-40 group-hover:opacity-100 transition-opacity p-0.5 rounded text-blue-500 hover:text-blue-600 hover:bg-blue-50 cursor-pointer">
              <Mail size={13} />
            </button>
          </div>
        ) : null;
      case 'phone':
        return client.phone ? (
          <div className="flex items-center gap-1.5">
            <a href={`tel:${client.phone}`} className="text-blue-600 hover:underline" onClick={e => e.stopPropagation()}>{client.phone}</a>
            <button onClick={e => { e.stopPropagation(); if (getWhatsAppNumber(client)) setWhatsappPanelClient(client); }}
              title={getWhatsAppNumber(client) ? 'Send WhatsApp' : 'Not a valid WhatsApp number'}
              disabled={!getWhatsAppNumber(client)}
              className={`opacity-40 group-hover:opacity-100 transition-opacity p-0.5 rounded ${getWhatsAppNumber(client) ? 'text-[#25D366] hover:bg-green-50 cursor-pointer' : 'text-gray-300 cursor-not-allowed'}`}>
              <IoLogoWhatsapp size={13} />
            </button>
          </div>
        ) : null;
      case 'relationshipType': case 'leadStatus': case 'leadSource': case 'companyStage':
        return client[column.key] ? <span className="px-2 py-1 text-xs font-medium rounded-full bg-white border">{client[column.key].replace(/_/g, ' ')}</span> : null;
      case 'legalType': return client.legalType ? client.legalType.replace(/_/g, ' ') : null;
      case 'employeeCount': return client.employeeCount ? client.employeeCount.replace('RANGE_', '').replace('_', '–') : null;
      case 'annualRevenue': return client.annualRevenue ? client.annualRevenue.replace('RANGE_', '$').replace('_', '–') : null;
      case 'createdAt': return client.createdAt ? new Date(client.createdAt).toLocaleDateString() : 'N/A';
      case 'updatedAt': return client.updatedAt ? new Date(client.updatedAt).toLocaleDateString() : 'N/A';
      default: return client[column.key] || null;
    }
  };

  const hasActiveFilters = filters.industry || filters.leadStatus || filters.leadSource || filters.companyName || filters.relationshipType || filters.companyStage || filters.legalType || filters.employeeCount || filters.annualRevenue || filters.paymentTerms || filters.contactOwner || filters.websiteUrl || filters.email || filters.phone || filters.primaryContact || filters.jobTitle;

  return (
    <div>
      {/* ── Email Modals ── */}
      {showBulkEmailModal && (
        <BulkEmailModal selectedCount={selectedRows.length} selectedIds={selectedRows} entityType="CLIENT" onClose={() => setShowBulkEmailModal(false)} />
      )}
      {showExcelEmailModal && (
        <ExcelEmailModal entityType="CLIENT" onClose={() => setShowExcelEmailModal(false)} />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-6 bg-white border-b gap-4">
        <div className="flex items-center gap-2">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Clients</h1>
          <span className="text-xs sm:text-sm text-gray-600">{totalElements} records</span>
          {loading && <span className="text-xs sm:text-sm text-blue-600">Loading...</span>}
        </div>
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <button onClick={() => setShowExcelEmailModal(true)}
           className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 whitespace-nowrap">
             Bulk Email
          </button>
          <button onClick={() => setShowImportModal(true)} className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 whitespace-nowrap">Import</button>
          <button onClick={() => setShowCreateModal(true)} className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 whitespace-nowrap">Create</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center border-b overflow-x-auto">
        {tabs.map(tab => (
          <button key={tab} onClick={() => { setActiveTab(tab); setSelectedRows([]); setCurrentPage(0); }}
            className={`px-4 sm:px-6 py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {tab}
            {tab === 'All clients' && activeTab === tab && <X className="ml-2 inline-block w-4 h-4 text-gray-400" />}
          </button>
        ))}
        <div className="ml-auto px-4 sm:px-6">
          <button onClick={() => setShowColumnSelector(true)} className="text-xs sm:text-sm text-blue-600 hover:text-blue-700 whitespace-nowrap">
            <span className="hidden sm:inline">Customize Columns</span><span className="sm:hidden">Columns</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="p-4 border-b bg-gray-50">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="w-48"><Select options={industryOptions} value={filters.industry} onChange={v => handleFilterChange('industry', v)} placeholder="Industry" isClearable className="text-sm" /></div>
          <div className="w-48"><Select options={leadStatusOptions} value={filters.leadStatus} onChange={v => handleFilterChange('leadStatus', v)} placeholder="Lead Status" isClearable className="text-sm" /></div>
          <div className="w-48"><Select options={leadSourceOptions} value={filters.leadSource} onChange={v => handleFilterChange('leadSource', v)} placeholder="Lead Source" isClearable className="text-sm" /></div>
          <button onClick={() => setShowMoreFilters(!showMoreFilters)} className="flex items-center gap-1 px-3 py-2 text-sm text-blue-600 border border-blue-300 rounded-md"><Plus size={16} /> More Filters</button>
          {hasActiveFilters && <button onClick={clearAllFilters} className="px-3 py-2 text-sm text-red-600 border border-red-300 rounded-md">Clear All</button>}
        </div>
        {showMoreFilters && (
          <div className="grid grid-cols-4 gap-3 mt-4 p-4 bg-white rounded-md border">
            <div><label className="block text-xs text-gray-600 mb-1">Relationship Type</label><Select options={relationshipTypeOptions} value={filters.relationshipType} onChange={v => handleFilterChange('relationshipType', v)} placeholder="Select..." isClearable className="text-sm" /></div>
            <div><label className="block text-xs text-gray-600 mb-1">Company Stage</label><Select options={companyStageOptions} value={filters.companyStage} onChange={v => handleFilterChange('companyStage', v)} placeholder="Select..." isClearable className="text-sm" /></div>
            <div><label className="block text-xs text-gray-600 mb-1">Legal Type</label><Select options={legalTypeOptions} value={filters.legalType} onChange={v => handleFilterChange('legalType', v)} placeholder="Select..." isClearable className="text-sm" /></div>
            <div><label className="block text-xs text-gray-600 mb-1">Employee Count</label><Select options={employeeCountOptions} value={filters.employeeCount} onChange={v => handleFilterChange('employeeCount', v)} placeholder="Select..." isClearable className="text-sm" /></div>
            <div><label className="block text-xs text-gray-600 mb-1">Annual Revenue</label><Select options={annualRevenueOptions} value={filters.annualRevenue} onChange={v => handleFilterChange('annualRevenue', v)} placeholder="Select..." isClearable className="text-sm" /></div>
            <div><label className="block text-xs text-gray-600 mb-1">Payment Terms</label><Select options={paymentTermsOptions} value={filters.paymentTerms} onChange={v => handleFilterChange('paymentTerms', v)} placeholder="Select..." isClearable className="text-sm" /></div>
            <div><label className="block text-xs text-gray-600 mb-1">Assigned To</label><Select options={employeeOptions} value={filters.contactOwner} onChange={v => handleFilterChange('contactOwner', v)} placeholder="Assigned To" isClearable className="text-sm" /></div>
            {[['companyName','Company Name','Search name...'],['websiteUrl','Website','Search website...'],['email','Email','Search email...'],['phone','Phone','Search phone...'],['primaryContact','Primary Contact','Search contact...'],['jobTitle','Job Title','Search title...']].map(([key,label,ph]) => (
              <div key={key}><label className="block text-xs text-gray-600 mb-1">{label}</label><input type="text" value={filters[key]} onChange={e => handleFilterChange(key, e.target.value)} placeholder={ph} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" /></div>
            ))}
          </div>
        )}
      </div>

      {/* Search and Bulk Actions */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between p-4 border-b gap-4">
        <div className="relative flex-1 max-w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input type="text" placeholder="Search name, phone, or domain" value={searchQuery} onChange={e => handleSearchChange(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm" />
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
          <button onClick={handleExport} className="px-3 sm:px-4 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 whitespace-nowrap font-bold">Export</button>
          <button onClick={() => { if (!selectedRows.length) { alert("Please select at least one client to edit"); return; } setSelectedClientsForEdit(clients.filter(c => selectedRows.includes(c.id))); setShowEditModal(true); }}
            className="px-3 sm:px-4 py-2 text-xs sm:text-sm bg-yellow-500 text-white rounded-md hover:bg-yellow-600 whitespace-nowrap">Bulk Edit</button>
          <button onClick={() => { if (selectedRows.length) setShowDeleteModal(true); else alert("Please select at least one client to delete"); }}
            className="px-3 sm:px-4 py-2 text-xs sm:text-sm bg-red-500 text-white rounded-md hover:bg-red-600 whitespace-nowrap">Bulk Delete</button>
          <button onClick={() => { if (selectedRows.length) setShowAssignModal(true); else alert("Please select at least one client to assign"); }}
            className="px-3 sm:px-4 py-2 text-xs sm:text-sm bg-purple-500 text-white rounded-md hover:bg-purple-600 whitespace-nowrap">Assign</button>
          {selectedRows.length > 0 && (
            <button onClick={() => setShowBulkEmailModal(true)}
              className="flex items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 whitespace-nowrap transition-colors">
              <Mail size={14} /> Send Email ({selectedRows.length})
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white">
        <table className="w-full min-w-max">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="w-12 p-3 bg-gray-50">
                <input type="checkbox" checked={selectedRows.length === clients.length && clients.length > 0} onChange={handleSelectAll} className="w-4 h-4 text-blue-600 rounded border-gray-300" />
              </th>
              {allColumns.filter(col => isColumnVisible(col.key)).map(column => (
                <th key={column.key} className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px]">
                  <div className="flex items-center gap-2">{column.label}<ArrowUpDown size={12} className="text-gray-400" /></div>
                </th>
              ))}
              <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {clients.length === 0 ? (
              <tr><td colSpan={visibleColumns.length + 2} className="p-8 text-center text-gray-500">{loading ? 'Loading clients...' : 'No clients found'}</td></tr>
            ) : (
              clients.map(client => (
                <tr key={client.id}
                  className={`group hover:bg-gray-50 ${selectedRows.includes(client.id) ? 'bg-blue-50' : ''}`}>
                  <td className="p-3"><input type="checkbox" checked={selectedRows.includes(client.id)} onChange={() => handleRowSelect(client.id)} className="w-4 h-4 text-blue-600 rounded border-gray-300" /></td>
                  {allColumns.filter(col => isColumnVisible(col.key)).map(column => (
                    <td key={column.key} className="p-3 text-xs text-gray-900">{renderCellContent(column, client)}</td>
                  ))}
                  <td className="px-4 py-4 whitespace-nowrap sticky right-0 bg-white group-hover:bg-gray-50 transition-colors shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.04)]">
                    <div className="flex items-center gap-1 text-slate-500">
                      <button onClick={e => { e.stopPropagation(); setSelectedClientForPreview(client); setShowPreviewModal(true); }} className="p-1.5 rounded-md hover:bg-blue-50 hover:text-blue-600 transition-colors cursor-pointer" title="Preview"><Eye size={16} /></button>
                      <button onClick={e => { e.stopPropagation(); setEditDrawerClient(client); }} className="p-1.5 rounded-md hover:bg-blue-50 hover:text-blue-600 transition-colors cursor-pointer" title="Edit"><Pencil size={16} /></button>
                      <button onClick={e => { e.stopPropagation(); setSingleDeleteClient(client); setShowSingleDeleteModal(true); }} className="p-1.5 rounded-md hover:bg-red-50 hover:text-red-600 transition-colors cursor-pointer" title="Delete"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <PageNavigator
        currentPage={currentPage}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={PAGE_SIZE}
        onPageChange={setCurrentPage}
      />

      {/* Column Selector */}
      {showColumnSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b"><h3 className="text-lg font-semibold">Edit columns</h3><button onClick={() => setShowColumnSelector(false)}><X size={20} /></button></div>
            <div className="p-4">
              <div className="flex gap-2 mb-4">
                <button onClick={handleSelectAllColumns} className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">Select all</button>
                <button onClick={handleDeselectAllColumns} className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">Deselect all</button>
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {allColumns.map(column => (
                  <label key={column.key} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input type="checkbox" checked={isColumnVisible(column.key)} onChange={() => !column.required && handleColumnToggle(column.key)} disabled={column.required} className="rounded border-gray-300 text-blue-600" />
                    <span className="text-sm">{column.label}</span>
                    {column.required && <span className="text-xs text-gray-500">(Required)</span>}
                  </label>
                ))}
              </div>
            </div>
            <div className="p-4 border-t flex justify-end"><button onClick={() => setShowColumnSelector(false)} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">Done</button></div>
          </div>
        </div>
      )}

      {/* Single Delete */}
      {showSingleDeleteModal && singleDeleteClient && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100"><h3 className="text-base font-semibold text-gray-900">Delete Client</h3></div>
            <div className="px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"><Trash2 size={18} className="text-red-600" /></div>
                <div>
                  <p className="text-sm text-gray-700">Are you sure you want to delete <span className="font-semibold text-gray-900">{singleDeleteClient.companyName}</span>?</p>
                  <p className="text-xs text-gray-500 mt-1">This action cannot be undone.</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button onClick={() => { setShowSingleDeleteModal(false); setSingleDeleteClient(null); }} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 font-medium">Cancel</button>
              <button onClick={handleSingleDelete} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium">Delete</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && <DeleteClientModal selectedRows={selectedRows} onClose={() => setShowDeleteModal(false)} onDelete={handleBulkDelete} />}
      {showAssignModal && <AssignClientModal selectedRows={selectedRows} onClose={() => setShowAssignModal(false)} onAssign={handleAssign} />}
      {showEditModal && <EditClientModal selectedItems={selectedClientsForEdit} clientIds={selectedRows} onClose={() => { setShowEditModal(false); setSelectedClientsForEdit([]); }} onSave={handleBulkEdit} />}
      {showPreviewModal && selectedClientForPreview && (
        <ClientPreview client={selectedClientForPreview} onClose={() => { setShowPreviewModal(false); setSelectedClientForPreview(null); }}
          onEdit={() => { setShowPreviewModal(false); setEditDrawerClient(selectedClientForPreview); }}
          onDelete={() => { setShowPreviewModal(false); setSingleDeleteClient(selectedClientForPreview); setShowSingleDeleteModal(true); }} />
      )}
      {showCreateModal && <CreateClientModal onClose={() => setShowCreateModal(false)} onCreate={() => { setShowCreateModal(false); refreshClients(); }} />}
      {showImportModal && <ImportClientsModal onClose={() => setShowImportModal(false)} onImport={() => { setShowImportModal(false); refreshClients(); }} />}
      {editDrawerClient && (
        <ClientEditDrawer client={editDrawerClient} onClose={() => setEditDrawerClient(null)}
          onSave={(updated) => { setClients(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c)); setEditDrawerClient(null); }} />
      )}

      <EmailTemplatePanel
        isOpen={Boolean(emailPanelClient)}
        onClose={() => setEmailPanelClient(null)}
        module="CRM"
        context={emailPanelClient ? {
          clientName: emailPanelClient.companyName || '',
          senderName: localStorage.getItem('email') || '',
          agencyName: localStorage.getItem('organizationName') || '',
        } : {}}
        recipient={emailPanelClient ? {
          email: emailPanelClient.primaryContactEmail || emailPanelClient.email,
          name: emailPanelClient.companyName || '',
        } : null}
      />
      <WhatsAppTemplatePanel
        isOpen={Boolean(whatsappPanelClient)}
        onClose={() => setWhatsappPanelClient(null)}
        module="CRM"
        context={whatsappPanelClient ? {
          clientName: whatsappPanelClient.companyName || '',
          senderName: localStorage.getItem('email') || '',
          agencyName: localStorage.getItem('organizationName') || '',
        } : {}}
        recipient={whatsappPanelClient ? {
          phone: getWhatsAppNumber(whatsappPanelClient),
          phoneSource: 'phone',
          name: whatsappPanelClient.companyName || '',
        } : null}
      />
    </div>
  );
};

export default Client;